import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface GalleryImage {
  url: string;
  promptKey: string;
}

interface GalleryCardProps {
  image: GalleryImage;
  index: number;
}

function GalleryCard({ image, index }: GalleryCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(image.promptKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = image.promptKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleUsePrompt = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem('ai_prefill_prompt', image.promptKey);
    navigate('/generator');
  };

  return (
    <div
      key={index}
      className="break-inside-avoid mb-4 group cursor-pointer"
      onClick={handleUsePrompt}
    >
      <div className="relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200">
        <img
          src={image.url}
          alt={image.promptKey}
          loading="lazy"
          className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
          {/* Prompt Text */}
          <p className="text-white text-xs leading-relaxed line-clamp-3 font-medium mb-3">
            {image.promptKey}
          </p>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30'
              }`}
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`}></i>
              {copied ? t('gallery.copied') : t('gallery.copyPrompt')}
            </button>

            {/* Use Prompt Button */}
            <button
              onClick={handleUsePrompt}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary rounded-lg text-xs font-bold text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-magic-line text-sm"></i>
              {t('gallery.usePrompt')}
            </button>
          </div>
        </div>

        {/* Top-right prompt icon badge */}
        <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <i className="ri-quill-pen-line text-white text-sm"></i>
        </div>
      </div>
    </div>
  );
}

export default function GallerySection() {
  const { t } = useTranslation();

  const images: GalleryImage[] = [
    {
      url: 'https://public.readdy.ai/ai/img_res/f5c24a308977d6a3b11d19264d8e0c04.jpg',
      promptKey: 'Futuristic megacity at golden hour with vertical gardens and flying vehicles',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=A%20lone%20female%20warrior%20standing%20on%20a%20cliff%20edge%20overlooking%20a%20vast%20enchanted%20forest%2C%20wearing%20ornate%20silver%20armor%20with%20glowing%20runes%2C%20long%20flowing%20crimson%20cape%20billowing%20in%20the%20wind%2C%20dramatic%20storm%20clouds%20gathering%20behind%20her%2C%20epic%20fantasy%20digital%20painting%2C%20rich%20jewel%20tones%2C%20highly%20detailed%2C%20cinematic%20composition%2C%20simple%20misty%20background&width=600&height=900&seq=gallery_002&orientation=portrait',
      promptKey: 'Epic fantasy female warrior in silver armor on a cliff with enchanted forest',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Macro%20photography%20of%20a%20single%20dewdrop%20on%20a%20spider%20web%20at%20sunrise%2C%20inside%20the%20droplet%20a%20perfect%20reflection%20of%20a%20blooming%20cherry%20blossom%20garden%2C%20soft%20bokeh%20background%20in%20pastel%20pink%20and%20green%2C%20ultra%20sharp%20focus%2C%20award%20winning%20nature%20photography%2C%20minimalist%20clean%20white%20background%2C%20ethereal%20and%20delicate%20mood&width=600&height=600&seq=gallery_003&orientation=squarish',
      promptKey: 'Macro dewdrop on spider web reflecting cherry blossom garden at sunrise',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=A%20majestic%20white%20tiger%20with%20electric%20blue%20glowing%20stripes%20prowling%20through%20a%20neon-lit%20cyberpunk%20jungle%2C%20bioluminescent%20plants%20surrounding%20it%2C%20rain%20falling%20in%20slow%20motion%2C%20dramatic%20side%20lighting%2C%20hyper-realistic%20fur%20detail%2C%20dark%20atmospheric%20background%20with%20deep%20teal%20and%20electric%20blue%20tones%2C%20cinematic%20wildlife%20photography%20style&width=600&height=750&seq=gallery_004&orientation=portrait',
      promptKey: 'White tiger with glowing blue stripes in a neon cyberpunk jungle',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=An%20elegant%20haute%20couture%20fashion%20editorial%2C%20model%20wearing%20an%20avant-garde%20sculptural%20white%20dress%20made%20of%20origami%20paper%20folds%2C%20standing%20in%20a%20minimalist%20Japanese%20zen%20garden%20with%20raked%20white%20sand%2C%20soft%20diffused%20natural%20light%2C%20high%20fashion%20photography%2C%20clean%20neutral%20background%2C%20sophisticated%20and%20artistic%20composition%2C%20Vogue%20magazine%20style&width=600&height=850&seq=gallery_005&orientation=portrait',
      promptKey: 'Haute couture origami paper dress in minimalist Japanese zen garden',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Underwater%20scene%20of%20an%20ancient%20sunken%20library%20with%20towering%20bookshelves%20filled%20with%20glowing%20manuscripts%2C%20colorful%20tropical%20fish%20swimming%20between%20the%20shelves%2C%20shafts%20of%20turquoise%20light%20filtering%20down%20from%20the%20surface%2C%20coral%20growing%20on%20the%20stone%20columns%2C%20magical%20and%20mysterious%20atmosphere%2C%20highly%20detailed%20digital%20art%2C%20rich%20teal%20and%20gold%20color%20palette&width=600&height=700&seq=gallery_006&orientation=portrait',
      promptKey: 'Ancient sunken library underwater with glowing manuscripts and tropical fish',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=A%20cozy%20futuristic%20treehouse%20nestled%20in%20a%20giant%20glowing%20bioluminescent%20tree%20at%20night%2C%20warm%20amber%20light%20spilling%20from%20round%20windows%2C%20rope%20bridges%20connecting%20multiple%20levels%2C%20fireflies%20dancing%20in%20the%20air%2C%20a%20small%20waterfall%20nearby%2C%20magical%20forest%20atmosphere%2C%20Studio%20Ghibli%20inspired%20illustration%20style%2C%20rich%20deep%20greens%20and%20warm%20golden%20tones%2C%20simple%20dark%20sky%20background&width=600&height=800&seq=gallery_007&orientation=portrait',
      promptKey: 'Cozy futuristic treehouse in bioluminescent tree at night, Ghibli style',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=A%20hyperrealistic%20portrait%20of%20an%20elderly%20astronaut%20with%20weathered%20face%20and%20kind%20eyes%2C%20wearing%20a%20vintage%20NASA%20spacesuit%20helmet%2C%20reflection%20of%20a%20distant%20nebula%20visible%20in%20the%20visor%2C%20deep%20wrinkles%20telling%20stories%20of%20a%20lifetime%20of%20exploration%2C%20dramatic%20chiaroscuro%20lighting%2C%20photorealistic%20skin%20texture%2C%20dark%20space%20background%20with%20subtle%20star%20field%2C%20emotional%20and%20powerful%20composition&width=600&height=750&seq=gallery_008&orientation=portrait',
      promptKey: 'Hyperrealistic elderly astronaut portrait with nebula reflection in visor',
    },
  ];

  return (
    <section className="py-24 lg:py-40 bg-[#1a1b1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <span className="inline-block px-5 py-2.5 bg-[#252830] border border-gray-700 rounded-full text-base font-bold mb-5 text-gray-300">
            {t('gallery.badge')}
          </span>
          <h2 className="text-5xl lg:text-6xl font-display font-black text-white mb-8">
            {t('gallery.title')}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-semibold">
            {t('gallery.subtitle')}
          </p>
          {/* Tip hint */}
          <p className="mt-4 text-sm text-gray-500 font-medium flex items-center justify-center gap-2">
            <i className="ri-cursor-line"></i>
            {t('gallery.hoverTip')}
          </p>
        </div>

        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {images.map((image, index) => (
            <GalleryCard key={index} image={image} index={index} />
          ))}
        </div>

        <div className="text-center mt-16">
          <a
            href="/generator"
            className="inline-flex items-center gap-3 px-10 py-5 bg-primary text-white rounded-full font-bold text-xl hover:bg-primary-600 transition-all hover:shadow-2xl hover:shadow-primary/40 cursor-pointer whitespace-nowrap"
          >
            {t('gallery.cta')}
            <i className="ri-arrow-right-line text-2xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
